/**
 * Push notification client-side utilities.
 * Handles permission requests, push subscription, and registering the
 * subscription with the ordering backend for later use by its admin-notification
 * triggers.
 */

import { savePushSubscription, deletePushSubscription } from '../lib/pos-api';

/**
 * Check if the browser supports the Push API and service workers.
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Request notification permission from the user.
 * Returns the permission state: 'granted', 'denied', or 'default'.
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.warn('Push: Notifications not supported in this browser.');
    return 'denied';
  }

  const permission = await Notification.requestPermission();
  return permission;
}

/**
 * Subscribe to push notifications via the Push API.
 * Requires a VAPID public key (base64url-encoded).
 * Returns the PushSubscription object or null on failure.
 */
export async function subscribeToPush(vapidPublicKey) {
  try {
    const registration = await navigator.serviceWorker.ready;

    // Check for existing subscription first
    const existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) {
      return existingSubscription;
    }

    // Convert VAPID key from base64url to Uint8Array
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });

    return subscription;
  } catch (error) {
    console.error('Push: Failed to subscribe:', error);
    return null;
  }
}

/**
 * Register the push subscription with the ordering backend for server-side push
 * triggers. The backend identifies the user from the POS access token, so we
 * only send the subscription itself. Returns true on success.
 */
export async function saveSubscription(subscription, accessToken) {
  if (!subscription || !accessToken) return false;

  try {
    await savePushSubscription(subscription, accessToken);
    return true;
  } catch (error) {
    console.error('Push: Failed to save subscription:', error);
    return false;
  }
}

/**
 * Tear down push notifications on this device: unsubscribe from the browser's
 * push service and remove the subscription from the backend. Best-effort — a
 * backend failure still leaves the browser unsubscribed. Returns true unless the
 * browser-side unsubscribe itself failed.
 */
export async function unsubscribeFromPush(accessToken) {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return true;

    const { endpoint } = subscription;
    const unsubscribed = await subscription.unsubscribe();

    if (accessToken && endpoint) {
      await deletePushSubscription(endpoint, accessToken).catch((error) => {
        console.error('Push: Failed to remove subscription from backend:', error);
      });
    }

    return unsubscribed;
  } catch (error) {
    console.error('Push: Failed to unsubscribe:', error);
    return false;
  }
}

/**
 * Convert a base64url-encoded string to a Uint8Array.
 * Required for the applicationServerKey in push subscription.
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
